import React, { useState } from "react";
import { Edit2, Trash2, Check, X, BookOpen } from "lucide-react";
import { Card, CardContent, Badge, IconButton, Tooltip } from "../ui";

export default function SubjectCard({ subject, canManage, onEdit, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDelete = async () => {
    await onDelete(subject._id);
    setConfirmDelete(false);
  };

  const subjectColor = subject.color || "#3B82F6";

  return (
    <Card className="relative flex h-full flex-col rounded-none border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-none transition-all duration-150 hover:border-neutral-300 hover:shadow-sm">
      {/* Top accent border line using subject color */}
      <div
        className="h-1 w-full shrink-0"
        style={{ backgroundColor: subjectColor }}
        aria-hidden="true"
      />

      <CardContent className="flex h-full flex-col justify-between space-y-4 p-4 sm:p-5">
        <div className="space-y-2.5">
          {/* Title & Subject Metadata */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="flex min-w-0 items-center gap-2 text-sm font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
              <BookOpen className="h-4 w-4 shrink-0 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
              <span className="truncate">{subject.name}</span>
            </h3>

            <div className="flex items-center gap-1.5 shrink-0">
              {/* Color Dot Indicator */}
              <span
                className="h-2.5 w-2.5 rounded-full border border-black/10 shrink-0"
                style={{ backgroundColor: subjectColor }}
                title={`Subject color: ${subjectColor}`}
              />
              <Badge variant="outline" className="font-mono uppercase text-[11px] rounded-none border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 px-1.5 py-0.5 font-semibold text-neutral-700 dark:text-neutral-200">
                {subject.code}
              </Badge>
            </div>
          </div>

          {/* Subject Description */}
          {subject.description && (
            <p className="line-clamp-2 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
              {subject.description}
            </p>
          )}
        </div>

        {/* Footer Actions & Elective/Core Tag */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-100 dark:border-neutral-800 pt-3">
          <div>
            {subject.isElective ? (
              <Badge variant="warning" className="rounded-none font-medium">
                Elective
              </Badge>
            ) : (
              <Badge variant="primary" className="rounded-none font-medium">
                Core
              </Badge>
            )}
          </div>

          {canManage && (
            <div className="flex items-center gap-1">
              {confirmDelete ? (
                <div className="flex items-center gap-1 border border-danger-200 bg-danger-50 p-1">
                  <span className="px-1 text-xs font-semibold text-danger-700">Delete?</span>
                  <IconButton
                    size="xs"
                    variant="danger"
                    aria-label="Confirm delete subject"
                    onClick={handleDelete}
                    className="rounded-none"
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  </IconButton>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label="Cancel delete"
                    onClick={() => setConfirmDelete(false)}
                    className="rounded-none"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </IconButton>
                </div>
              ) : (
                <>
                  <Tooltip content="Edit subject">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Edit subject"
                      onClick={() => onEdit(subject)}
                      className="rounded-none hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    >
                      <Edit2 className="h-3.5 w-3.5 text-neutral-600 dark:text-neutral-300" aria-hidden="true" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip content="Delete subject">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Delete subject"
                      onClick={() => setConfirmDelete(true)}
                      className="rounded-none hover:bg-danger-50 hover:text-danger-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}